import React from 'react';

import { Button, LinkButton } from '@eika/button';
import { CheckmarkAnimation, CheckmarkAnimationTypes } from '@eika/checkmark-animation';
import { Icon, IconTypes } from '@eika/icon';
import { Link, LinkType } from '@eika/link';

import CenteredModalContent from '../../components/shared/CenteredModalContent/CenteredModalContent';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { useConfig } from '../../defaultConfig';
import type { ApprovalStatus } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';

type RequestExceptionWithResponse = import('@eika-infrastruktur/request').RequestExceptionWithResponse;

export const showTransactionResult = (
    paymentStatus?: ApprovalStatus,
    paymentError?: RequestExceptionWithResponse,
): boolean => !!paymentError || (!!paymentStatus && ['COMPLETED', 'REJECTED', 'UNKNOWN'].includes(paymentStatus));

interface Props {
    paymentStatus?: ApprovalStatus;
    paymentError?: RequestExceptionWithResponse;
    retry: () => void;
    onClose: () => void;
}

const UpdatePaymentLimitsResult: React.FC<Props> = ({ paymentError, paymentStatus, retry, onClose }) => {
    const translated = useTranslation();
    const { frontPageLink } = useConfig();

    if (paymentError) {
        return (
            <CenteredModalContent>
                <ErrorCardWrapper exception={paymentError} isModal>
                    <Button onClick={retry}>{translated.ok}</Button>
                </ErrorCardWrapper>
            </CenteredModalContent>
        );
    }

    if (paymentStatus === 'REJECTED') {
        return (
            <CenteredModalContent withMessage>
                <Icon className="wallet-questionmark" type={IconTypes.UTROPSTEGN_36} />
                <h3 className="typography-h3">{translated.updatePaymentLimits.result.rejectedTitle}</h3>
                <p>{translated.updatePaymentLimits.result.rejectedInfo}</p>
                <Button onClick={onClose} size="small">
                    {translated.updatePaymentLimits.result.check}
                </Button>
            </CenteredModalContent>
        );
    }

    return (
        <CenteredModalContent withMessage>
            <CheckmarkAnimation
                className="transfer-success-checkmark"
                checkmarkAnimationType={CheckmarkAnimationTypes.POSITIVE}
            />
            <h3 className="typography-h3">{translated.updatePaymentLimits.result.success}</h3>
            <section className="update-payment-limits__result-submit-wrapper">
                <LinkButton onClick={onClose}>{translated.updatePaymentLimits.result.close}</LinkButton>
                <Link linkType={LinkType.BUTTON_STANDARD_POSITIVE} href={frontPageLink?.url ?? '/'}>
                    {translated.updatePaymentLimits.result.toFrontPage}
                </Link>
            </section>
        </CenteredModalContent>
    );
};

export default UpdatePaymentLimitsResult;
